<template>
  <a-form 
    ref="formRef" :model="formState" :rules="rules"
    layout="vertical" class="mfa-input-form" @finish="handleConfirmSendEmail">
    <div class="mfa-step">
      <div class="mfa-step_header">
        <div>
          <img :src="lockIcon" width="64" alt="Google Auth" />
        </div>
        <h2>
          Se ha iniciado la autenticación en 2 pasos (2FA)
        </h2>
        <p>
          Enviaremos un código de verificación al siguiente correo electrónico <br/>
          para validar que sea el correcto.
        </p>
      </div>
      <div class="form">
        <a-form-item ref="emailRef" label="Correo electrónico" required name="email">
          <a-input v-model:value="formState.email" placeholder="Ingresar correo" />
        </a-form-item>
        <a-alert style="margin-top: 15px" type="warning" show-icon >
          <template #message>
            Si tu correo actual es uno autogenerado, asegúrate de 
            ingresar un <span style="font-weight: 600;">correo válido</span>. Recuerda que este correo nuevo, reemplazará al actual.
          </template>
        </a-alert>
      </div>
      <div class="mfa-step_footer">
        <a-button
          :loading="loading"
          type="primary"
          html-type="submit"
          :disabled="!isValidCodeRequired"
        >
          Enviar código
        </a-button>
      </div>
      <!-- <div class="mfa-help">
        <a-typography-link @click="showModal = true">
          Necesito ayuda con mi código
        </a-typography-link>
      </div> -->
    </div>
  </a-form>
  
  <!-- <a-modal
    v-model:visible="showModal"
    width="560px"
    class="help"
    :footer="null"
    centered
  >
    <div>
      <img :src="lockIcon" width="64" alt="Google Auth" />
    </div>
    <h2 class="help_title">
      Ayuda con mi código
    </h2>
    <p>
      Si perdiste el acceso a tu código de verificación o presentas otros 
      inconvenientes relacionados a este, puedes realizar lo siguiente:
    </p>
    <a-card style="margin-bottom: 10px">
      <h3 class="help_subtitle">
        <QuestionCircleOutlined />
        Contactar a Soporte Beex
      </h3>
      <span>
        Solicita ayuda a tu administrador para que te comunique con 
        nuestra área de soporte, o genere un ticket de ayuda.
      </span>
    </a-card>
    <a-card style="margin-bottom: 20px">
      <h3 class="help_subtitle">
        <LockOutlined />
        Reiniciar autenticación en 2 pasos (2FA)
      </h3>
      <span>
        Si perdiste tu token, solicita a tu administrador que te genere 
        una nueva contraseña para reiniciar la autenticación en 2 pasos.
      </span>
    </a-card>
    <h4>
      Para mayor soporte, contáctanos al siguiente número:
    </h4>
    <a :href="whatsappSupport" target="_blank" style="font-size: 18px; font-weight: 600;">
      <WhatsAppOutlined />
      +51 {{ numberSupport }}
    </a>
  </a-modal> -->
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, watch, createVNode } from 'vue'
import lockIcon from "@/app/auth/assets/lock_icon.png?url";
import { mfaService } from "@/app/auth/services/MfaService";
import { MfaType } from '../../utils/enums';
import { message, Modal } from 'ant-design-vue';
import { ResponseError } from '@/app/shared/utils/httpManager';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

defineProps({
  hideBack: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["next", "setEmail"]);

// const showModal = ref(false);
// const whatsappSupport = `https://wa.me/51${import.meta.env.VITE_SUPPORT_WHATSAPP}`;
// const numberSupport = import.meta.env.VITE_SUPPORT_WHATSAPP;

const formRef = ref();
const formState = ref({
  email: ''
});

const emailRef = ref();

const rules = {
  email: [
    {
      required: true,
      message: "Ingrese un correo válido",
      type: "email",
      trigger: ["blur", "change"],
    },
  ]
};

const isValidCodeRequired = ref(false);
const loading = ref(false);

watch(
  () => formState.value.email,
  async () => {
    if (formRef.value) {
      try {
        await formRef.value.validate();
        isValidCodeRequired.value = true;
      } catch {
        isValidCodeRequired.value = false;
      }
    } else {
      isValidCodeRequired.value = false;
    }
  },
  { immediate: true }
);

const handleSendEmail = async () => {
  const storedData = window.atob(sessionStorage.getItem("authenticationData") || '');
  const userData = JSON.parse(storedData);
  try {
    loading.value = true;
    await mfaService.generateToken({
      username: userData?.username,
      password: userData?.password,
      mfa_type: MfaType.Email,
      email: formState.value.email,
    });

    emit('setEmail', formState.value.email);
    emit('next');
  } catch (error) {
    const messageError =
      error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

    message.error(messageError);
  } finally {
    loading.value = false;
  }
}

const handleConfirmSendEmail = async () => {
  console.log("handleConfirmSendEmail");
  if (isValidCodeRequired.value) {
    Modal.confirm({
      title: `Enviaremos el código de verificación a “${formState.value.email}”.
        ¿Confirmas que es un correo válido?`,
      content: "Asegúrate de haber ingresado un correo válido, de lo contrario, no recibirás ningún código.",
      icon: createVNode(QuestionCircleOutlined),
      onOk: async () => await handleSendEmail(),
      centered: true,
      okText: "Sí, enviar código",
      cancelText: "No, cambiar correo",
    });
  }
}

// Enfocar el primer input al montar el componente
onMounted(() => {
  const storedData = window.atob(sessionStorage.getItem("authenticationData") || '');
  const userData = JSON.parse(storedData);
  formState.value.email = userData?.username || '';
  nextTick(() => {
    emailRef.value?.focus();
  })
})

defineExpose({
  email: formState.value.email,
});
</script>

<style scoped lang="sass">
.mfa-step
  text-align: center
  flex-direction: column
  display: flex
  gap: 24px
  justify-content: center
  justify-items: center
  align-items: center
  &_header
    display: flex
    flex-direction: column
    gap: 16px
    text-align: center
  h2
    width: 400px
    font-weight: 600
    margin-bottom: 0
    font-size: 24px
    line-height: 32px
  p
    margin: 0
    color: #000000A6
    font-size: 14px
  .form
    width: 420px
    text-align: left
    :deep(.ant-form-item)
      width: 100%
      margin-bottom: 5px

  .mfa-code-input
    width: 36px
    height: 48px
    text-align: center
    margin: 0 10px
    border-radius: 6px !important

  &_footer
    width: 420px
    display: flex
    flex-direction: column
    gap: 16px
    text-align: center

  .mfa-stores
    display: flex
    justify-content: center
    gap: 1rem

    img
      height: 40px

  .mfa-help
    margin-top: 8px
    font-size: 14px
    color: #000000A6
    display: flex
    gap: 8px
    align-items: center
    a
      display: flex
      gap: 6px
      align-items: center
</style>
